/**
 * Canonical public surface URLs for cross-domain deploys (e.g. xo.watch vs xo-call.com).
 *
 * Safe configuration only: `VITE_PUBLIC_*` full URLs or origins, plus optional
 * `VITE_PUBLIC_HUB_URL` as a shared prefix for hub-mounted routes when per-surface
 * env vars are unset.
 *
 * Does not read API secrets, vault credentials, or private auth env.
 */

function trimEnv(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function stripTrailingSlash(s: string): string {
  return s.replace(/\/+$/, "");
}

function joinOriginPath(origin: string, path: string): string {
  const o = stripTrailingSlash(origin);
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${o}${p}`;
}

function pickFullOrOriginPath(fullEnv: unknown, originEnv: unknown, path: string): string | undefined {
  const full = trimEnv(fullEnv);
  if (full) return stripTrailingSlash(full);
  const origin = trimEnv(originEnv);
  if (origin) return joinOriginPath(origin, path);
  return undefined;
}

/** Set at build time when both watch + call builds should share one hub origin prefix. */
function configuredPublicHubOrigin(): string | undefined {
  const raw = trimEnv(import.meta.env.VITE_PUBLIC_HUB_URL);
  if (!raw) return undefined;
  return stripTrailingSlash(raw);
}

function withHubFallback(fallbackPath: string, direct?: string): string {
  if (direct) return direct;
  const hub = configuredPublicHubOrigin();
  if (hub) return joinOriginPath(hub, fallbackPath);
  return fallbackPath;
}

export function isPublicAbsoluteHttpUrl(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

export function homeUrl(): string {
  const direct = pickFullOrOriginPath(import.meta.env.VITE_PUBLIC_HOME_URL, import.meta.env.VITE_PUBLIC_HOME_ORIGIN, "/");
  if (direct) return direct;
  const hub = configuredPublicHubOrigin();
  if (hub) return `${hub}/`;
  return "/";
}

export function proofUrl(): string {
  return withHubFallback(
    "/xo-proof",
    pickFullOrOriginPath(import.meta.env.VITE_PUBLIC_PROOF_URL, import.meta.env.VITE_PUBLIC_PROOF_ORIGIN, "/xo-proof"),
  );
}

export function missionUrl(): string {
  return withHubFallback(
    "/mission",
    pickFullOrOriginPath(import.meta.env.VITE_PUBLIC_MISSION_URL, import.meta.env.VITE_PUBLIC_MISSION_ORIGIN, "/mission"),
  );
}

export function vaultUrl(): string {
  return withHubFallback(
    "/vault",
    pickFullOrOriginPath(import.meta.env.VITE_PUBLIC_VAULT_URL, import.meta.env.VITE_PUBLIC_VAULT_ORIGIN, "/vault"),
  );
}

export function ledgerUrl(): string {
  return withHubFallback(
    "/ledger",
    pickFullOrOriginPath(import.meta.env.VITE_PUBLIC_LEDGER_URL, import.meta.env.VITE_PUBLIC_LEDGER_ORIGIN, "/ledger"),
  );
}

export function pulseUrl(): string {
  return withHubFallback(
    "/pulse",
    pickFullOrOriginPath(import.meta.env.VITE_PUBLIC_PULSE_URL, import.meta.env.VITE_PUBLIC_PULSE_ORIGIN, "/pulse"),
  );
}

export function trustContinuumUrl(): string {
  return withHubFallback(
    "/trust-continuum",
    pickFullOrOriginPath(import.meta.env.VITE_PUBLIC_TRUST_URL, import.meta.env.VITE_PUBLIC_TRUST_ORIGIN, "/trust-continuum"),
  );
}

export function callUrl(): string {
  return withHubFallback(
    "/call",
    pickFullOrOriginPath(import.meta.env.VITE_PUBLIC_CALL_URL, import.meta.env.VITE_PUBLIC_CALL_ORIGIN, "/call"),
  );
}

export function callLandingUrl(): string {
  return withHubFallback(
    "/call/landing",
    pickFullOrOriginPath(
      import.meta.env.VITE_PUBLIC_CALL_LANDING_URL,
      import.meta.env.VITE_PUBLIC_CALL_LANDING_ORIGIN,
      "/call/landing",
    ),
  );
}

/** Merge query pairs onto a public surface URL (absolute or root-relative). */
export function appendUrlQuery(base: string, query: Record<string, string | undefined | null>): string {
  const pairs = Object.entries(query).filter(([, v]) => v != null && String(v).trim().length) as [string, string][];
  if (!pairs.length) return base;

  if (isPublicAbsoluteHttpUrl(base)) {
    const u = new URL(base);
    for (const [k, v] of pairs) u.searchParams.set(k, String(v));
    return u.toString();
  }

  const qs = new URLSearchParams();
  for (const [k, v] of pairs) qs.set(k, String(v));
  const tail = qs.toString();
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${tail}`;
}

function splitRoutePathAndTail(routePath: string): { path: string; tail: string } {
  const i = routePath.search(/[?#]/);
  if (i === -1) return { path: routePath, tail: "" };
  return { path: routePath.slice(0, i), tail: routePath.slice(i) };
}

function joinResolvedBaseWithSuffix(base: string, canonicalPrefix: string, fullPathname: string): string {
  if (!isPublicAbsoluteHttpUrl(base)) return fullPathname;
  const suffix = fullPathname === canonicalPrefix ? "" : fullPathname.slice(canonicalPrefix.length);
  return `${stripTrailingSlash(base)}${suffix}`;
}

export function publicProofPathname(pathname: string): string {
  const p = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (!p.startsWith("/xo-proof")) return p;
  return joinResolvedBaseWithSuffix(proofUrl(), "/xo-proof", p);
}

export function publicCallPathname(pathname: string): string {
  const p = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (!p.startsWith("/call")) return p;
  return joinResolvedBaseWithSuffix(callUrl(), "/call", p);
}

export function publicTrustPathname(pathname: string): string {
  const p = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (!p.startsWith("/trust-continuum")) return p;
  return joinResolvedBaseWithSuffix(trustContinuumUrl(), "/trust-continuum", p);
}

function resolveMappedPrefixPath(pathname: string): string | null {
  const p = pathname.startsWith("/") ? pathname : `/${pathname}`;

  if (p === "/mission" || p.startsWith("/mission/")) {
    return joinResolvedBaseWithSuffix(missionUrl(), "/mission", p);
  }
  if (p === "/vault" || p.startsWith("/vault/")) {
    return joinResolvedBaseWithSuffix(vaultUrl(), "/vault", p);
  }
  if (p === "/ledger" || p.startsWith("/ledger/")) {
    return joinResolvedBaseWithSuffix(ledgerUrl(), "/ledger", p);
  }
  if (p === "/pulse" || p.startsWith("/pulse/")) {
    return joinResolvedBaseWithSuffix(pulseUrl(), "/pulse", p);
  }
  return null;
}

/**
 * Resolve a routed pathname (no query/hash) to a canonical public URL when configured,
 * otherwise return the original pathname (same-origin SPA navigation).
 */
export function resolvePublicNavHref(pathname: string): string {
  const raw = pathname.trim() || "/";
  const path = raw.split(/[?#]/, 1)[0] || "/";
  if (path === "/" || path === "") return homeUrl();
  if (path === "/xo-proof" || path.startsWith("/xo-proof/")) return publicProofPathname(path);
  if (path === "/call" || path.startsWith("/call/")) return publicCallPathname(path);
  if (path === "/trust-continuum" || path.startsWith("/trust-continuum/")) return publicTrustPathname(path);

  const mapped = resolveMappedPrefixPath(path);
  return mapped ?? path;
}

/** Like {@link resolvePublicNavHref} but preserves trailing `?query` / `#hash` fragments. */
export function resolvePublicNavHrefFull(routePath: string): string {
  const { path, tail } = splitRoutePathAndTail(routePath);
  const resolved = resolvePublicNavHref(path);
  if (!tail) return resolved;
  if (!isPublicAbsoluteHttpUrl(resolved)) {
    return `${resolved}${tail}`;
  }
  if (tail.startsWith("#")) {
    return `${resolved}${tail}`;
  }
  if (tail.startsWith("?")) {
    try {
      const u = new URL(resolved);
      const extra = new URLSearchParams(tail.slice(1));
      extra.forEach((v, k) => {
        u.searchParams.set(k, v);
      });
      return u.toString();
    } catch {
      return `${resolved}${tail}`;
    }
  }
  return `${resolved}${tail}`;
}
